import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Clock, Zap, Filter, ChevronDown } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { useNoshStore } from '../../lib/store';
import { ProgressiveImage } from './progressive-image';
import type { Recipe } from '../../lib/store';

interface MealPlanRecipeSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectRecipe: (recipe: Recipe) => void;
  selectedMealType?: 'breakfast' | 'lunch' | 'dinner' | 'dessert' | 'snack';
  selectedDate?: string;
}

const MealPlanRecipeSelector: React.FC<MealPlanRecipeSelectorProps> = ({
  isOpen,
  onClose,
  onSelectRecipe,
  selectedMealType = 'lunch',
  selectedDate
}) => {
  const { recipes, loadRecipes } = useNoshStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'breakfast' | 'lunch' | 'dinner' | 'dessert' | 'snack'>('all');
  const [displayCount, setDisplayCount] = useState(12); // Initial load: 12 recipes
  const [isLoading, setIsLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Load recipes on mount
  useEffect(() => {
    if (isOpen) {
      loadRecipes();
      setDisplayCount(12); // Reset count when modal opens
      setSearchQuery('');
      setSelectedFilter('all');
    }
  }, [isOpen, loadRecipes]);

  // Debounced search
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setDisplayCount(12); // Reset pagination when searching
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      // Search will be handled by filteredRecipes
    }, 300);
  }, []);

  // Filter recipes based on search and meal type
  const filteredRecipes = React.useMemo(() => {
    let filtered = recipes;

    // Apply meal type filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(recipe => 
        recipe.mealType?.toLowerCase() === selectedFilter ||
        (recipe.tags && recipe.tags.some(tag => tag.toLowerCase().includes(selectedFilter)))
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(recipe =>
        recipe.title.toLowerCase().includes(query) ||
        recipe.ingredients.some(ingredient => {
          const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name;
          return ingredientName.toLowerCase().includes(query);
        }) ||
        (recipe.tags && recipe.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    return filtered;
  }, [recipes, searchQuery, selectedFilter]);

  // Displayed recipes (with pagination)
  const displayedRecipes = filteredRecipes.slice(0, displayCount);
  const hasMoreRecipes = displayCount < filteredRecipes.length;
  const remainingRecipes = filteredRecipes.length - displayCount;

  // Load More Button Handler
  const handleLoadMore = () => {
    setIsLoading(true);
    setTimeout(() => {
      setDisplayCount(prev => prev + 8); // Load 8 more recipes as requested
      setIsLoading(false);
    }, 300);
  };

  // Handle recipe selection
  const handleSelectRecipe = (recipe: Recipe) => {
    onSelectRecipe(recipe);
    onClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Select Recipe for {selectedMealType?.charAt(0).toUpperCase() + selectedMealType?.slice(1)}
            </h2>
            {selectedDate && (
              <p className="text-sm text-gray-600 mt-1">
                {new Date(selectedDate).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={onClose}
            className="hover:bg-gray-100"
          >
            <X size={20} />
          </Button>
        </div>

        {/* Search & Filters */}
        <div className="p-6 pb-4 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search recipes by name, ingredient, or tag..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-4 py-2 border-gray-300 focus:border-primary focus:ring-primary"
              />
            </div>

            {/* Meal Type Filter */}
            <div className="relative">
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value as any)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:border-primary focus:ring-primary min-w-[120px]"
              >
                <option value="all">All Meals</option>
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="dessert">Dessert</option>
                <option value="snack">Snack</option>
              </select>
              <ChevronDown size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {displayedRecipes.length} of {filteredRecipes.length} recipes
            {searchQuery && ` for "${searchQuery}"`}
          </div>
        </div>

        {/* Recipe Grid */}
        <div className="p-6 max-h-[500px] overflow-y-auto">
          {displayedRecipes.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🍽️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recipes found</h3>
              <p className="text-gray-600">
                {searchQuery 
                  ? `Try adjusting your search term or filters`
                  : `You haven't created any recipes yet. Generate some recipes first!`}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {displayedRecipes.map((recipe) => (
                  <div
                    key={recipe.id}
                    onClick={() => handleSelectRecipe(recipe)}
                    className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg hover:border-primary/30 cursor-pointer transition-all duration-200 group"
                  >
                    {/* Recipe Image */}
                    <div className="relative aspect-square mb-3 rounded-lg overflow-hidden">
                      <ProgressiveImage
                        src={recipe.thumbnailUrl || recipe.imageUrl}
                        alt={recipe.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        placeholder="/img/small 1.png"
                      />
                    </div>

                    {/* Recipe Info */}
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {recipe.title}
                    </h3>

                    {/* Quick Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>{recipe.time}min</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Zap size={14} />
                        <span>{recipe.calories} cal</span>
                      </div>
                    </div>

                    {/* Difficulty Badge */}
                    <div className="mt-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        recipe.difficulty === 'easy' 
                          ? 'bg-green-100 text-green-800'
                          : recipe.difficulty === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {recipe.difficulty}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMoreRecipes && (
                <div className="text-center py-6">
                  <Button
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    variant="outline"
                    className="px-6 py-2 border-2 rounded-xl hover:bg-primary hover:text-white hover:border-primary transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Loading more recipes...
                      </>
                    ) : (
                      `Load More Recipes (${remainingRecipes} remaining)`
                    )}
                  </Button>
                </div>
              )}

              {/* End of Results */}
              {!hasMoreRecipes && displayedRecipes.length > 12 && (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-600">You've seen all {filteredRecipes.length} recipes!</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-gray-100">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Select a recipe to add to your meal plan
            </p>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealPlanRecipeSelector; 